/**
 * Unit categories. Linear categories define a `factor` per unit (base units per
 * 1 unit); temperature is handled specially. Unit *labels* are technical and
 * stay in English (km, lb, °C are universal); all surrounding UI text is i18n'd.
 */
import { m } from "@/paraglide/messages";
import { convertLinear, convertTemperature, type TempUnit } from "./convert";

export interface Unit {
  id: string;
  label: string;
  /** Base units per 1 of this unit (linear categories only). */
  factor?: number;
}

export interface UnitCategory {
  id: string;
  name: () => string;
  icon: string;
  kind: "linear" | "temperature";
  units: Unit[];
  defaultFrom: string;
  defaultTo: string;
  defaultValue: string;
}

export const CATEGORIES: Record<string, UnitCategory> = {
  length: {
    id: "length",
    name: () => m.uc_cat_length(),
    icon: "📏",
    kind: "linear",
    units: [
      { id: "mm", label: "Millimeter (mm)", factor: 0.001 },
      { id: "cm", label: "Centimeter (cm)", factor: 0.01 },
      { id: "m", label: "Meter (m)", factor: 1 },
      { id: "km", label: "Kilometer (km)", factor: 1000 },
      { id: "in", label: "Inch (in)", factor: 0.0254 },
      { id: "ft", label: "Foot (ft)", factor: 0.3048 },
      { id: "yd", label: "Yard (yd)", factor: 0.9144 },
      { id: "mi", label: "Mile (mi)", factor: 1609.344 },
    ],
    defaultFrom: "m",
    defaultTo: "ft",
    defaultValue: "1",
  },
  weight: {
    id: "weight",
    name: () => m.uc_cat_weight(),
    icon: "⚖️",
    kind: "linear",
    units: [
      { id: "mg", label: "Milligram (mg)", factor: 0.001 },
      { id: "g", label: "Gram (g)", factor: 1 },
      { id: "kg", label: "Kilogram (kg)", factor: 1000 },
      { id: "t", label: "Tonne (t)", factor: 1_000_000 },
      { id: "oz", label: "Ounce (oz)", factor: 28.349523125 },
      { id: "lb", label: "Pound (lb)", factor: 453.59237 },
      { id: "st", label: "Stone (st)", factor: 6350.29318 },
    ],
    defaultFrom: "kg",
    defaultTo: "lb",
    defaultValue: "1",
  },
  temperature: {
    id: "temperature",
    name: () => m.uc_cat_temperature(),
    icon: "🌡️",
    kind: "temperature",
    units: [
      { id: "c", label: "Celsius (°C)" },
      { id: "f", label: "Fahrenheit (°F)" },
      { id: "k", label: "Kelvin (K)" },
    ],
    defaultFrom: "c",
    defaultTo: "f",
    defaultValue: "100",
  },
  volume: {
    id: "volume",
    name: () => m.uc_cat_volume(),
    icon: "🧪",
    kind: "linear",
    units: [
      { id: "ml", label: "Milliliter (ml)", factor: 0.001 },
      { id: "l", label: "Liter (l)", factor: 1 },
      { id: "m3", label: "Cubic meter (m³)", factor: 1000 },
      { id: "tsp", label: "Teaspoon (US)", factor: 0.00492892 },
      { id: "tbsp", label: "Tablespoon (US)", factor: 0.0147868 },
      { id: "cup", label: "Cup (US)", factor: 0.236588 },
      { id: "pt", label: "Pint (US)", factor: 0.473176 },
      { id: "qt", label: "Quart (US)", factor: 0.946353 },
      { id: "gal", label: "Gallon (US)", factor: 3.785411784 },
    ],
    defaultFrom: "l",
    defaultTo: "gal",
    defaultValue: "1",
  },
  area: {
    id: "area",
    name: () => m.uc_cat_area(),
    icon: "🗺️",
    kind: "linear",
    units: [
      { id: "cm2", label: "Square centimeter (cm²)", factor: 0.0001 },
      { id: "m2", label: "Square meter (m²)", factor: 1 },
      { id: "ha", label: "Hectare (ha)", factor: 10000 },
      { id: "km2", label: "Square kilometer (km²)", factor: 1_000_000 },
      { id: "sqft", label: "Square foot (ft²)", factor: 0.09290304 },
      { id: "sqyd", label: "Square yard (yd²)", factor: 0.83612736 },
      { id: "acre", label: "Acre", factor: 4046.8564224 },
      { id: "sqmi", label: "Square mile (mi²)", factor: 2_589_988.110336 },
    ],
    defaultFrom: "m2",
    defaultTo: "sqft",
    defaultValue: "1",
  },
  speed: {
    id: "speed",
    name: () => m.uc_cat_speed(),
    icon: "🚀",
    kind: "linear",
    units: [
      { id: "mps", label: "Meters/second (m/s)", factor: 1 },
      { id: "kph", label: "Kilometers/hour (km/h)", factor: 0.277777778 },
      { id: "mph", label: "Miles/hour (mph)", factor: 0.44704 },
      { id: "fps", label: "Feet/second (ft/s)", factor: 0.3048 },
      { id: "knot", label: "Knot (kn)", factor: 0.514444444 },
    ],
    defaultFrom: "kph",
    defaultTo: "mph",
    defaultValue: "100",
  },
  data: {
    id: "data",
    name: () => m.uc_cat_data(),
    icon: "💾",
    kind: "linear",
    units: [
      { id: "B", label: "Byte (B)", factor: 1 },
      { id: "KB", label: "Kilobyte (KB)", factor: 1000 },
      { id: "MB", label: "Megabyte (MB)", factor: 1_000_000 },
      { id: "GB", label: "Gigabyte (GB)", factor: 1_000_000_000 },
      { id: "TB", label: "Terabyte (TB)", factor: 1_000_000_000_000 },
      { id: "KiB", label: "Kibibyte (KiB)", factor: 1024 },
      { id: "MiB", label: "Mebibyte (MiB)", factor: 1_048_576 },
      { id: "GiB", label: "Gibibyte (GiB)", factor: 1_073_741_824 },
    ],
    defaultFrom: "MB",
    defaultTo: "MiB",
    defaultValue: "1",
  },
  time: {
    id: "time",
    name: () => m.uc_cat_time(),
    icon: "⏱️",
    kind: "linear",
    units: [
      { id: "ms", label: "Millisecond (ms)", factor: 0.001 },
      { id: "s", label: "Second (s)", factor: 1 },
      { id: "min", label: "Minute (min)", factor: 60 },
      { id: "h", label: "Hour (h)", factor: 3600 },
      { id: "day", label: "Day", factor: 86400 },
      { id: "week", label: "Week", factor: 604800 },
    ],
    defaultFrom: "h",
    defaultTo: "min",
    defaultValue: "1",
  },
};

export const CATEGORY_LIST: UnitCategory[] = Object.values(CATEGORIES);

/** Safe lookup (`Object.hasOwn` so inherited keys don't resolve and 500). */
export function getCategory(id: string): UnitCategory | undefined {
  return Object.hasOwn(CATEGORIES, id) ? CATEGORIES[id] : undefined;
}

export function getUnit(cat: UnitCategory, id: string): Unit | undefined {
  return cat.units.find((u) => u.id === id);
}

/** Convert within a category, dispatching linear vs temperature. */
export function convert(
  cat: UnitCategory,
  value: number,
  fromId: string,
  toId: string,
): number | null {
  if (cat.kind === "temperature") {
    return convertTemperature(value, fromId as TempUnit, toId as TempUnit);
  }
  const from = getUnit(cat, fromId);
  const to = getUnit(cat, toId);
  if (from?.factor == null || to?.factor == null) return null;
  return convertLinear(value, from.factor, to.factor);
}
